import React from 'react';

import type { CanonicalResume } from '../schemas/resumeSchema';

interface ResumeCanvasProps {
  data: CanonicalResume;
  /** When true, drop outer card chrome so it sits inside the agent paper stage. */
  embedded?: boolean;
  className?: string;
}

export const ResumeCanvas: React.FC<ResumeCanvasProps> = ({ data, embedded = false, className = '' }) => {
  const wrapperClass = embedded
    ? `resume-canvas dark:text-[var(--ds-text-body)] text-slate-900 ${className}`
    : `resume-canvas p-8 bg-white dark:bg-[var(--ds-bg-card)] text-slate-900 dark:text-[var(--ds-text-body)] shadow-md max-w-4xl mx-auto ${className}`;

  return (
    <div className={wrapperClass} data-canonical-resume="true">
      <header className="border-b dark:border-white/12 pb-4 mb-4 text-center">
        <h1 className="text-2xl font-bold uppercase tracking-wide">
          {data.personalInfo.fullName || 'Candidate'}
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          {[
            data.personalInfo.email,
            data.personalInfo.phone,
            data.personalInfo.location,
            data.personalInfo.linkedin,
          ]
            .map((v) => v?.trim())
            .filter(Boolean)
            .join(' | ')}
        </p>
      </header>

      {data.professionalSummary.trim() ? (
        <section className="mb-4">
          <h2 className="text-md font-bold uppercase border-b pb-1 mb-2 text-slate-800">
            Professional Summary
          </h2>
          <p className="text-sm leading-relaxed">{data.professionalSummary}</p>
        </section>
      ) : null}

      {data.workExperience.length > 0 ? (
        <section className="mb-4">
          <h2 className="text-md font-bold uppercase border-b pb-1 mb-2 text-slate-800">
            Work Experience
          </h2>
          {data.workExperience.map((exp, idx) => (
            <div key={`${exp.company}-${exp.startDate}-${idx}`} className="mb-3">
              <div className="flex justify-between gap-3 text-sm font-semibold">
                <span>
                  {exp.title}
                  {exp.company ? (
                    <>
                      {' — '}
                      <span className="text-slate-700">{exp.company}</span>
                    </>
                  ) : null}
                </span>
                <span className="shrink-0 text-slate-500">
                  {exp.startDate}
                  {exp.endDate ? ` – ${exp.endDate}` : ''}
                </span>
              </div>
              {exp.highlights.length > 0 ? (
                <ul className="list-disc ml-5 text-sm space-y-1 mt-1 text-slate-700">
                  {exp.highlights.map((bullet, i) => (
                    <li key={i}>{bullet}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}

      {(data.skills.technical.length
        || data.skills.frameworksAndTools.length
        || data.skills.managementAndDomain.length) ? (
        <section className="mb-4">
          <h2 className="text-md font-bold uppercase border-b pb-1 mb-2 text-slate-800">Skills</h2>
          <div className="text-sm space-y-1">
            {data.skills.technical.length > 0 && (
              <p><strong>Technical:</strong> {data.skills.technical.join(', ')}</p>
            )}
            {data.skills.frameworksAndTools.length > 0 && (
              <p><strong>Frameworks & Tools:</strong> {data.skills.frameworksAndTools.join(', ')}</p>
            )}
            {data.skills.managementAndDomain.length > 0 && (
              <p><strong>Domain Expertise:</strong> {data.skills.managementAndDomain.join(', ')}</p>
            )}
          </div>
        </section>
      ) : null}

      {data.education.length > 0 ? (
        <section className="mb-4">
          <h2 className="text-md font-bold uppercase border-b pb-1 mb-2 text-slate-800">Education</h2>
          {data.education.map((edu, idx) => (
            <div key={`${edu.institution}-${edu.year}-${idx}`} className="flex justify-between gap-3 text-sm">
              <span>
                <strong>{edu.degree}</strong>
                {edu.institution ? `, ${edu.institution}` : ''}
              </span>
              <span className="shrink-0 text-slate-500">{edu.year}</span>
            </div>
          ))}
        </section>
      ) : null}

      {data.certifications?.length ? (
        <section className="mb-4">
          <h2 className="text-md font-bold uppercase border-b pb-1 mb-2 text-slate-800">Certifications</h2>
          <ul className="list-disc ml-5 text-sm space-y-1 text-slate-700">
            {data.certifications.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {data.awards?.length ? (
        <section className="mb-4">
          <h2 className="text-md font-bold uppercase border-b pb-1 mb-2 text-slate-800">Awards</h2>
          <ul className="list-disc ml-5 text-sm space-y-1 text-slate-700">
            {data.awards.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
};
